import { type RefOrGetter } from '@/helpers'
import { computed, type ComputedRef, type MaybeRefOrGetter, toValue } from 'vue'
import {
    type CssTransitionDto,
    type CssTransitionEasing,
    getCssTransition,
} from '@/composables/motion/css-transition.ts'

export function usePercentOpacity(options: {
    percent: RefOrGetter<number>
    endOpacity: MaybeRefOrGetter<number>
}): ComputedRef<number> {
    return computed(() => {
        const { percent, endOpacity } = options

        const opacityRange = 1 - toValue(endOpacity)
        return 1 - toValue(percent) * opacityRange
    })
}

export interface OpacityStyleOptions {
    opacity: RefOrGetter<number>
    transitionTime?: MaybeRefOrGetter<number>
    transitionEasing?: MaybeRefOrGetter<CssTransitionEasing>
}

export function useOpacityStyle(options: OpacityStyleOptions): {
    style: ComputedRef<string>
    opacityStyle: ComputedRef<string>
    transition: ComputedRef<CssTransitionDto | undefined>
    transitionStyle: ComputedRef<string | undefined>
} {
    const withDefaults: Required<OpacityStyleOptions> = {
        transitionTime: 200,
        transitionEasing: 'ease-in-out',
        ...options,
    }

    const { opacity, transitionTime, transitionEasing } = withDefaults

    const opacityStyle = computed(() => {
        return `opacity: ${toValue(opacity).toFixed(2)}`
    })

    const transition = computed((): CssTransitionDto | undefined => {
        const time = toValue(transitionTime)
        const easing = toValue(transitionEasing)

        if (time && easing) {
            return {
                property: 'opacity',
                time: time,
                easing: easing,
            }
        }

        return undefined
    })

    const transitionStyle = computed((): string | undefined => {
        const trans = transition.value

        if (trans) {
            return getCssTransition([trans])
        }

        return undefined
    })

    const style = computed(() => {
        const transition = transitionStyle.value

        if (transition) {
            return `${opacityStyle.value}; ${transition};`
        }

        return opacityStyle.value
    })

    return {
        style,
        opacityStyle,
        transition,
        transitionStyle,
    }
}
